import React, { useEffect, useMemo, useRef, useState } from "react";

const AVAILABLE_FILL = "#4CAF50";
const OCCUPIED_FILL = "#F44336";
const LABEL_FONT = "700 15px Roboto, sans-serif";
const WARNING_STROKE = "#F59E0B";

const Canvas = (props) => {
    const [blink, setBlink] = useState(true);
    const [hoveredSlot, setHoveredSlot] = useState(null);
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
    const [canvasStyle, setCanvasStyle] = useState({ height: "500px" });
    const [parsedSlots, setParsedSlots] = useState([]);
    const canvasRef = useRef(null);
    const imageRef = useRef(null);
    const [imageLoaded, setImageLoaded] = useState(false);

    const floor = props.floor;
    const enableTooltip = Boolean(props.slotDetails || props.showAvailabilityTooltip);
    const wronglyParkedSet = useMemo(
        () => new Set((props.wronglyParkedSlotNos || []).map((slotNo) => String(slotNo))),
        [props.wronglyParkedSlotNos]
    );

    useEffect(() => {
        if (!floor?.slots || !Array.isArray(floor.slots)) {
            setParsedSlots([]);
            return;
        }

        const parsed = floor.slots.map((row) => {
            let coords = null;
            if (row?.slot_coordinates) {
                try {
                    coords = JSON.parse(row.slot_coordinates);
                } catch (e) {
                    coords = null;
                }
            }
            return { ...row, __slot: coords };
        });

        setParsedSlots(parsed);
    }, [floor]);

    useEffect(() => {
        setBlink(true);

        if (!props.blink) return undefined;
        const intervalId = setInterval(() => setBlink((prev) => !prev), 500);
        return () => clearInterval(intervalId);
    }, [props.blink]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !floor?.floor_map_url) return;

        const img = new Image();
        img.src = floor.floor_map_url;
        img.onload = () => {
            imageRef.current = img;
            canvas.width = img.width;
            canvas.height = img.height;
            setImageLoaded(true);
        };
        img.onerror = () => {
            imageRef.current = null;
            setImageLoaded(false);
        };
    }, [floor?.floor_map_url]);

    useEffect(() => {
        if (props.fullScreen) {
            setCanvasStyle({ height: "100vh" });
        } else {
            setCanvasStyle({ height: `${props.scale * 500}px` });
        }
    }, [props.fullScreen, props.scale]);

    const visibleSlots = useMemo(() => {
        return parsedSlots.filter((row) => {
            const isAvailable = Number(row.is_available) === 1;
            const isWronglyParked = wronglyParkedSet.has(String(row.slot_no));

            if (props.statusFilter === "available") return isAvailable;
            if (props.statusFilter === "occupied") return !isAvailable;
            if (props.statusFilter === "wronglyParked") return isWronglyParked;
            return true;
        });
    }, [parsedSlots, props.statusFilter, wronglyParkedSet]);

    useEffect(() => {
        const canvas = canvasRef.current;
        const context = canvas?.getContext?.("2d");
        const img = imageRef.current;

        if (!canvas || !context || !floor || !img || !imageLoaded) return;

        context.clearRect(0, 0, canvas.width, canvas.height);
        context.drawImage(img, 0, 0);
        context.textAlign = "center";
        context.textBaseline = "middle";

        for (const row of visibleSlots) {
            const slot = row.__slot;
            if (!slot) continue;

            const isAvailable = Number(row.is_available) === 1;
            const isSelected = props.selectedSlotNo && props.selectedSlotNo === row.slot_no;
            const isHovered = hoveredSlot?.slot_no === row.slot_no;
            const isWronglyParked = wronglyParkedSet.has(String(row.slot_no));

            if (isAvailable && props.blink && !blink) continue;

            context.globalAlpha = 1;
            context.fillStyle = isAvailable ? AVAILABLE_FILL : OCCUPIED_FILL;
            context.fillRect(slot.x, slot.y, slot.w, slot.h);

            context.globalAlpha = 1;
            context.lineWidth = isSelected ? 4 : isWronglyParked ? 3.5 : isHovered ? 3 : 1.5;
            context.strokeStyle = isSelected
                ? "#0f172a"
                : isWronglyParked
                  ? WARNING_STROKE
                  : "#ffffff";
            context.strokeRect(slot.x, slot.y, slot.w, slot.h);

            if (props.showLabels && slot.w >= 30 && slot.h >= 18) {
                context.font = LABEL_FONT;
                context.fillStyle = "#ffffff";
                context.strokeStyle = "rgba(15, 23, 42, 0.72)";
                context.lineWidth = 4;
                const labelX = slot.x + slot.w / 2;
                const labelY = slot.y + slot.h / 2;
                const label = String(row.slot_no ?? "");
                context.strokeText(label, labelX, labelY);
                context.fillText(label, labelX, labelY);
            }
        }
    }, [
        blink,
        floor,
        hoveredSlot,
        imageLoaded,
        props.blink,
        props.selectedSlotNo,
        props.showLabels,
        visibleSlots,
        wronglyParkedSet,
    ]);

    const getInteractedSlot = (e) => {
        const canvas = canvasRef.current;
        if (!canvas || !floor) return null;

        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        const mouseX = (e.clientX - rect.left) * scaleX;
        const mouseY = (e.clientY - rect.top) * scaleY;

        const target = visibleSlots.find((row) => {
            const slot = row.__slot;
            if (!slot) return false;
            return (
                mouseX >= slot.x &&
                mouseX <= slot.x + slot.w &&
                mouseY >= slot.y &&
                mouseY <= slot.y + slot.h
            );
        });

        return {
            rect,
            slot: target || null,
        };
    };

    const handleMouseMove = (e) => {
        const result = getInteractedSlot(e);
        if (!result) return;

        if (result.slot) {
            setHoveredSlot(result.slot);
            setTooltipPosition({
                x: e.clientX - result.rect.left,
                y: e.clientY - result.rect.top,
            });
        } else {
            setHoveredSlot(null);
        }
    };

    const handleMouseLeave = () => setHoveredSlot(null);

    const handleClick = (e) => {
        const result = getInteractedSlot(e);
        if (!result?.slot) return;

        if (props.onSlotSelect) {
            props.onSlotSelect(result.slot);
        }

        if (props.getCameraFeed) {
            props.getCameraFeed(result.slot);
        }
    };

    const activeTooltipSlot = hoveredSlot || visibleSlots.find((slot) => slot.slot_no === props.selectedSlotNo);

    return (
        <div style={{ position: "relative", display: "inline-block", maxWidth: "100%" }}>
            <canvas
                ref={canvasRef}
                style={{
                    ...canvasStyle,
                    zIndex: 100,
                    maxWidth: "100%",
                    borderRadius: 18,
                    boxShadow: "0 20px 50px rgba(15, 23, 42, 0.14)",
                    cursor: props.getCameraFeed || props.onSlotSelect ? "pointer" : "default",
                }}
                height={1000}
                width={1000}
                onClick={handleClick}
                {...(enableTooltip && {
                    onMouseMove: handleMouseMove,
                    onMouseLeave: handleMouseLeave,
                })}
            />

            {enableTooltip && activeTooltipSlot && hoveredSlot && (
                <div
                    style={{
                        position: "absolute",
                        top: tooltipPosition.y,
                        left: tooltipPosition.x,
                        background:
                            "linear-gradient(145deg, rgba(15, 23, 42, 0.95), rgba(30, 41, 59, 0.94))",
                        color: "white",
                        minHeight: props.slotDetails ? "150px" : undefined,
                        minWidth: props.slotDetails ? "190px" : 170,
                        maxWidth: "280px",
                        padding: "12px 14px",
                        borderRadius: "12px",
                        pointerEvents: "none",
                        transform: "translate(7%, 0%)",
                        zIndex: 1000,
                        boxShadow: "0 20px 40px rgba(15, 23, 42, 0.28)",
                    }}
                >
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>
                        Slot: {activeTooltipSlot?.slot_no ?? "N/A"}
                    </div>

                    {props.slotDetails ? (
                        <p style={{ textAlign: "start", wordWrap: "break-word", marginBottom: 0 }}>
                            Controller : {activeTooltipSlot?.details?.controller ?? "N/A"} <br />
                            Controller MAC : {activeTooltipSlot?.details?.controller_mac ?? "N/A"} <br />
                            Camera : {activeTooltipSlot?.details?.camera ?? "N/A"} <br />
                            Camera IP : {activeTooltipSlot?.details?.camera_ip ?? "N/A"} <br />
                            Channel : {activeTooltipSlot?.details?.channel ?? "N/A"} <br />
                            Device Address : {activeTooltipSlot?.details?.device_address ?? "N/A"} <br />
                        </p>
                    ) : (
                        <p style={{ textAlign: "start", wordWrap: "break-word", marginBottom: 0 }}>
                            Status :{" "}
                            {Number(activeTooltipSlot?.is_available) === 1 ? "Available" : "Occupied"}
                            {Number(activeTooltipSlot?.is_available) === 1 ? null : (
                                <>
                                    <br />
                                    Vehicle No : {activeTooltipSlot?.vehicle_no || "N/A"}
                                </>
                            )}
                            {wronglyParkedSet.has(String(activeTooltipSlot?.slot_no)) ? (
                                <>
                                    <br />
                                    Warning : Same vehicle is occupying multiple slots
                                </>
                            ) : null}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

export default Canvas;
